import {
    clampToMaxPeriod,
    isValidDateRange,
    MAX_PERIOD_DAYS,
} from '~utils/date-picker-helpers';
import {
    analyticsToday,
    ApiPeriod,
    campaignRange,
    CampaignWindow,
    isApiPeriod,
    previousDay,
    resolvePeriodRequest,
} from '../kpis/periods';
import type { ResolvedPeriod } from '../kpis/types';

export interface PageFilterParams {
    period?: unknown;
    startDate?: string;
    endDate?: string;
}

/**
 * A campaign dashboard's campaign. `campaignId` names a campaign the API
 * serves; without one the period travels as its dates.
 */
export interface PageCampaign {
    window: CampaignWindow;
    campaignId?: string;
}

export interface PageFilter {
    pageRange: ResolvedPeriod;
    /** The bar's capsule this period is, absent for a hand-picked range. */
    preset?: ApiPeriod;
}

const DEFAULT_PERIOD: ApiPeriod = '7d';

const LEGACY_TRAILING: readonly [number, ApiPeriod][] = [
    [1, 'today'],
    [7, '7d'],
    [30, '30d'],
    [90, '90d'],
];

/** Presets a dated range can coincide with, checked in this order. */
const DATED_PRESETS: readonly ApiPeriod[] = [
    '7d',
    '30d',
    '90d',
    'last-week',
    'last-month',
    'campaign',
];

const lengthInDays = (start: string, end: string): number =>
    (Date.parse(end) - Date.parse(start)) / 86_400_000 + 1;

const presetRange = (
    preset: ApiPeriod,
    today: string,
    campaign: PageCampaign | undefined
): ResolvedPeriod | undefined => {
    if (preset !== 'campaign') {
        return resolvePeriodRequest({ period: preset }, today);
    }
    if (!campaign) return undefined;
    const range = campaignRange(campaign.window, today);
    const fits = lengthInDays(range.start, range.end) <= MAX_PERIOD_DAYS;
    return campaign.campaignId && fits
        ? {
              ...range,
              preset: { period: 'campaign', campaignId: campaign.campaignId },
          }
        : clampToMaxPeriod(range);
};

/** A link from before the presets moved to the API: dates through today. */
const legacyPreset = (
    start: string,
    end: string,
    today: string,
    campaign: PageCampaign | undefined
): ApiPeriod | undefined => {
    if (end < today) return undefined;
    if (campaign && start === campaign.window.startDate) return 'campaign';
    const length = lengthInDays(start, today);
    return LEGACY_TRAILING.find(([days]) => days === length)?.[1];
};

/**
 * The page's period: the `period` preset, else a hand-picked range ending
 * yesterday at the latest, else the page default. A range that is exactly a
 * preset's window is that preset, so it is served from the preloaded key. A
 * hand-edited range that is inverted or one-sided falls back to the default.
 */
export const resolvePageFilter = (
    params: PageFilterParams,
    defaultPeriod: ApiPeriod = DEFAULT_PERIOD,
    campaign?: PageCampaign,
    today: string = analyticsToday()
): PageFilter => {
    const fromPreset = (preset: ApiPeriod): PageFilter | undefined => {
        const range = presetRange(preset, today, campaign);
        return range && { pageRange: range, preset };
    };

    if (isApiPeriod(params.period)) {
        const filter = fromPreset(params.period);
        if (filter) return filter;
    }

    if (isValidDateRange(params)) {
        const start = params.startDate as string;
        const legacy = legacyPreset(
            start,
            params.endDate as string,
            today,
            campaign
        );
        const filter = legacy && fromPreset(legacy);
        if (filter) return filter;

        const yesterday = previousDay(today);
        const end =
            (params.endDate as string) < yesterday
                ? (params.endDate as string)
                : yesterday;
        if (start <= end) {
            const range = clampToMaxPeriod({ start, end });
            const same = DATED_PRESETS.map(fromPreset).find(
                (filter) =>
                    filter?.pageRange.start === range.start &&
                    filter.pageRange.end === range.end
            );
            return same ?? { pageRange: range };
        }
    }

    return (
        fromPreset(defaultPeriod) ?? (fromPreset(DEFAULT_PERIOD) as PageFilter)
    );
};

/**
 * A hand-edited `?provinces=` param can arrive as a boolean/number. Only a string
 * or string[] is a usable region selection; anything else is dropped so the region
 * falls back to National rather than feeding junk to the province resolver.
 */
export const coerceQueryProvinces = (
    raw: unknown
): string | string[] | undefined =>
    typeof raw === 'string' || Array.isArray(raw) ? raw : undefined;
